import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Partnership } from 'app/partials/join-today/types/partner.model';
import { WipperozAlertService } from 'app/wipperoz-core/services/alert.service';
import { IpLocationService } from 'app/wipperoz-core/services/ip-location.service';
import { LoggingService } from 'app/wipperoz-core/services/logging.service';
import { WipperozApiService } from 'app/wipperoz-core/services/wipperoz-api.service';
import { IpLocation } from 'app/wipperoz-core/types/location/ip-location.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, filter, retryWhen, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ResourcesService {
  onTermsChanged: BehaviorSubject<any>;
  onPartnershipsChanged: BehaviorSubject<any>;
  onPrivacyChanged: BehaviorSubject<any>;
  onUserStatisticsChanged: BehaviorSubject<any>;
  onContactUsChanged: BehaviorSubject<any>;
  onUnsubscribeChanged: BehaviorSubject<any>;
  countUsers = 0;
  countries = [];
  countLocations = 0;
  categoriesAll = [];
  uniqueProfessions: {
    categoryEn: string;
    categoryES: string;
  }[] = [];
  uniqueCoordinates: {
    lat: number;
    lng: number;
  }[] = [];

  isValidEmailChanged: BehaviorSubject<boolean>;
  isValidEmail: boolean;
  isBrowserOutdated = false;

  constructor(
    private _wipperozApiService: WipperozApiService,
    private wipperozAlertService: WipperozAlertService,
    private loggingService: LoggingService,
    private ipLocationService: IpLocationService,
    @Inject(LOCALE_ID) private localeId: string
  ) {
    this.onTermsChanged = new BehaviorSubject<any>(null);
    this.onPrivacyChanged = new BehaviorSubject<any>(null);
    this.onUserStatisticsChanged = new BehaviorSubject<any>(null);
    this.onContactUsChanged = new BehaviorSubject<any>(null);
    this.onUnsubscribeChanged = new BehaviorSubject<any>(null);
    this.onPartnershipsChanged = new BehaviorSubject<any>(null);

    this.isValidEmail = false;
    this.isValidEmailChanged = new BehaviorSubject<boolean>(this.isValidEmail);
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
    const dataType = route.data['dataType'];
    return new Promise((resolve, reject) => {
      if (dataType === 'wipperoz-terms') {
        this.getTerms().then((data) => {
          resolve(data);
        }, reject);
      }

      if (dataType === 'wipperoz-privacy') {
        this.getPrivacy().then((data) => {
          resolve(data);
        }, reject);
      }
    });
  }

  getTerms(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get('/resources/terms')
        .pipe(filter((response: any) => response !== undefined))
        .subscribe((response: any) => {
          response = response.items.filter((element) => {
            return element.language.toUpperCase() === this.localeId.toLocaleUpperCase();
          });
          if (response !== undefined && response.length > 0) {
            this.onTermsChanged.next(response);
            resolve(response);
          } else {
            this.onTermsChanged.next(null);
            resolve(null);
          }
        }),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  getPrivacy(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get('/resources/privacy')
        .pipe(filter((response: any) => response !== undefined))
        .subscribe((response: any) => {
          response = response.items.filter((element) => {
            return element.language.toUpperCase() === this.localeId.toLocaleUpperCase();
          });
          if (response !== undefined && response.length > 0) {
            this.onPrivacyChanged.next(response);
            resolve(response);
          } else {
            this.onPrivacyChanged.next(null);
            resolve(null);
          }
        }),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  async getPartnerships(): Promise<Partnership[]> {
    let params = new HttpParams();
    params = params.append('language', this.localeId);

    return await this.ipLocationService
      .getLocation()
      .pipe(
        filter((x: any) => {
          return x !== undefined;
        }),
        retryWhen((errors) => errors.pipe(delay(3000), take(2)))
      )
      .toPromise()
      .then((x) => {
        this.ipLocationService.ipLocation = new IpLocation(x.items[0]);

        this.ipLocationService.ipLocationChanged.next(x);
        return new Promise((resolve, reject) => {
          this._wipperozApiService
            .get('/resources/partnerships', params)
            .pipe(filter((response: { items: Partnership[] }) => response !== undefined))
            .subscribe((response: { items: Partnership[] }) => {
              const data = response.items.filter((element) => {
                return element.language.toUpperCase() === this.localeId.toLocaleUpperCase();
              });
              if (data !== undefined && data.length > 0) {
                const countryCode =
                  this.ipLocationService.ipLocation !== undefined
                    ? this.ipLocationService.ipLocation.countryCode.toUpperCase()
                    : 'NOCOUNTRY';

                const result = data.filter((item) => {
                  if (
                    item.countries.filter((el) => el.country.toUpperCase() === countryCode).length > 0 ||
                    item.global
                  ) {
                    return item;
                  }
                });

                this.onPartnershipsChanged.next(result);
                resolve(result);
              } else {
                this.onPartnershipsChanged.next(null);
                resolve(null);
              }
            }),
            (err) => {
              of(`I caught: ${err}`);
              return reject;
            };
        });
      });
  }

  async getUserStatistics(): Promise<any> {
    return await this.ipLocationService
      .getLocation()
      .pipe(
        filter((x: any) => {
          return x !== undefined;
        }),
        retryWhen((errors) => errors.pipe(delay(3000), take(2)))
      )
      .toPromise()
      .then((x) => {
        this.ipLocationService.ipLocation = new IpLocation(x.items[0]);
        this.ipLocationService.ipLocationChanged.next(x);
        return new Promise((resolve, reject) => {
          if (this.countUsers === 0) {
            let params = new HttpParams();

            params = params.append(
              'country',
              this.ipLocationService.ipLocation !== undefined
                ? this.ipLocationService.ipLocation.country.toLowerCase()
                : 'NOCOUNTRY'
            );

            params = params.append('language', this.localeId);

            this._wipperozApiService
              .get<any>('/resources/users-statistics', params)
              .pipe(filter((response: any) => response !== undefined))
              .subscribe((response: any) => {
                if (response !== undefined) {
                  this.countUsers = response.countUsers;
                  this.countLocations = response.countLocations;
                  this.countries = response.countries;
                  this.categoriesAll = response.items;

                  this.categoriesAll.map((category) => {
                    if (!this.uniqueProfessions.find((o) => o.categoryEn === category.categoryEn)) {
                      this.uniqueProfessions.push({ categoryEn: category.categoryEn, categoryES: category.categoryEs });
                    }
                  });
                  this.onUserStatisticsChanged.next(response);
                  resolve(response);
                } else {
                  this.onUserStatisticsChanged.next(null);
                  resolve(null);
                }
              }),
              (err) => {
                of(`I caught: ${err}`);
                return reject;
              };
          } else {
            this.onUserStatisticsChanged.next({
              countUsers: this.countUsers,
              countLocations: this.countLocations,
              countries: this.countries,
              items: this.categoriesAll
            });
            resolve({
              countUsers: this.countUsers,
              countLocations: this.countLocations,
              countries: this.countries,
              items: this.categoriesAll
            });
          }
        });
      });
  }
  unsubscribeFromNotification(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .post('/resources/unsubscribe', data)
        .pipe(filter((response: any) => response !== undefined))
        .subscribe((response: any) => {
          this.onPrivacyChanged.next(response);
          resolve(response);
        }),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  saveContactMessage(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      data.language = this.localeId;
      this._wipperozApiService
        .post('/resources/contact-us', data)
        .pipe(filter((response: any) => response !== undefined))
        .subscribe((response: any) => {
          this.onPrivacyChanged.next(response);
          resolve(response);
        }),
        (err) => {
          of(`I caught: ${err}`);
          return reject;
        };
    });
  }

  checkEmailAddress(emailAddress: string): Promise<any> {
    let params = new HttpParams();

    params = params.append('emailAddress', emailAddress);

    return new Promise((resolve, reject) => {
      this._wipperozApiService
        .get<any>('/tools/landing/email-address-verification', params)
        .pipe(filter((response: any) => response !== undefined))
        .subscribe(
          (response: any) => {
            if (response.statusCode === 200) {
              this.isValidEmail = response.body.isValidEmail;
              resolve(this.isValidEmail);
              this.isValidEmailChanged.next(this.isValidEmail);
            } else {
              reject;
              this.isValidEmailChanged.next(this.isValidEmail);
            }
          },
          (error) => {
            this.wipperozAlertService.error(
              $localize`:@@checkEmailAddressError:Error checking email address. We are investigating the cause.`
            );
            this.loggingService.logError('resources.service: checkEmailAddress error', JSON.stringify(error));
            reject;
          }
        );
    });
  }
}
